import { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Compass, PaperPlaneTilt, Sparkle, TrendUp } from "@phosphor-icons/react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { discoverApi } from "@/lib/apiClient";
import PassportAppHeader from "@/components/PassportAppHeader";
import { usePassportShare } from "@/components/messages/PassportShareProvider";
import DiscoverPlanCard from "@/components/discover/DiscoverPlanCard";
import DiscoverToolbar from "@/components/discover/DiscoverToolbar";
import DiscoverEmptyState from "@/components/discover/DiscoverEmptyState";

const EMPTY_COPY = {
    trip: {
        title: "No public trips match this route yet",
        description: "Try a broader country filter, switch to Dates, or check back as more Passport travelers publish their Concierge itineraries.",
        ctaLabel: "Open Concierge",
        ctaHref: "/concierge",
    },
    traveldate: {
        title: "No public dates match this route yet",
        description: "TravelDate creators haven’t published plans for this filter combination yet. Try another city or head into TravelDate Concierge.",
        ctaLabel: "Open TravelDate",
        ctaHref: "/traveldate",
    },
};

export default function Discover() {
    const { user } = useAuth() || {};
    const { openPassportShare } = usePassportShare();
    const [type, setType] = useState("trip");
    const [sort, setSort] = useState("recommended");
    const [countryId, setCountryId] = useState("");
    const [cityId, setCityId] = useState("");
    const [countries, setCountries] = useState([]);
    const [cities, setCities] = useState([]);
    const [state, setState] = useState({ items: [], nextCursor: null, loading: true, loadingMore: false });
    const [busyMap, setBusyMap] = useState({});

    useEffect(() => {
        let active = true;
        discoverApi.countries(type).then((res) => {
            if (!active) return;
            const items = res.items || [];
            setCountries(items);
            if (countryId && !items.some((country) => country.country_id === countryId)) {
                setCountryId("");
                setCityId("");
            }
        }).catch(() => {
            if (!active) return;
            setCountries([]);
        });
        return () => {
            active = false;
        };
    }, [type]);

    useEffect(() => {
        if (!countryId) {
            setCities([]);
            return;
        }
        discoverApi.cities(countryId, type === "trip" ? "trip" : "traveldate")
            .then((res) => setCities(res.items || []))
            .catch(() => setCities([]));
    }, [countryId, type]);

    const loadItems = useCallback(async (cursor = null) => {
        const key = cursor ? "loadingMore" : "loading";
        setState((prev) => ({ ...prev, [key]: true }));
        try {
            const request = type === "trip" ? discoverApi.listTrips : discoverApi.listDates;
            const response = await request({ sort, country_id: countryId || undefined, city_id: cityId || undefined, cursor, limit: 18 });
            setState((prev) => ({
                items: cursor ? [...prev.items, ...(response.items || [])] : (response.items || []),
                nextCursor: response.next_cursor || null,
                loading: false,
                loadingMore: false,
            }));
        } catch {
            setState({ items: [], nextCursor: null, loading: false, loadingMore: false });
            toast.error("Passport Discover is unavailable right now.");
        }
    }, [cityId, countryId, sort, type]);

    useEffect(() => {
        loadItems();
    }, [loadItems]);

    const setBusy = (sourceId, value) => setBusyMap((prev) => ({ ...prev, [sourceId]: value }));

    const handleLike = async (item) => {
        if (!user) return toast.info("Sign in to like community plans.");
        try {
            const updater = item.content_type === "trip" ? discoverApi.likeTrip : discoverApi.likeDate;
            await updater(item.source_id, !item.viewer?.liked);
            await loadItems();
        } catch (error) {
            toast.error(error?.response?.data?.detail || "Could not update likes.");
        }
    };

    const handleAdd = async (item) => {
        if (!user) return toast.info("Sign in to add this plan to your Passport.");
        setBusy(item.source_id, true);
        try {
            const key = `${item.content_type}-${item.source_id}-${Date.now()}`;
            const action = item.content_type === "trip" ? discoverApi.addTripToItinerary : discoverApi.addDateToMyDates;
            await action(item.source_id, key);
            toast.success(item.content_type === "trip" ? "Added to My Trips" : "Added to My Dates");
            await loadItems();
        } catch (error) {
            toast.error(error?.response?.data?.detail || "Could not add this plan.");
        } finally {
            setBusy(item.source_id, false);
        }
    };

    const handleShare = (item) => {
        openPassportShare({
            shareRef: { entity_type: item.content_type === "trip" ? "trip" : "traveldate", entity_id: item.source_id },
            sourceLabel: item.content_type === "trip" ? "this Trip" : "this TravelDate",
        });
    };

    const stats = useMemo(() => {
        const totals = state.items.reduce((acc, item) => {
            acc.likes += item.metrics?.likes_count || 0;
            acc.adds += item.metrics?.additions_count || 0;
            return acc;
        }, { likes: 0, adds: 0 });
        return [
            { label: type === "trip" ? "Live trips" : "Live dates", value: state.items.length },
            { label: "Likes", value: totals.likes },
            { label: "Adds", value: totals.adds },
        ];
    }, [state.items, type]);

    const selectedCountry = useMemo(() => countries.find((item) => item.country_id === countryId) || null, [countries, countryId]);

    return (
        <div className="min-h-screen grain text-white" data-testid="discover-page">
            <PassportAppHeader homeHref="/app" subtitle="Discover" maxWidthClass="max-w-[1320px]" />
            <div className="max-w-[1320px] mx-auto px-4 sm:px-6 lg:px-8 pt-4 pb-12 space-y-6">
                <section className="rounded-[28px] overflow-hidden border p-5 sm:p-6 lg:p-7" style={{ background: "linear-gradient(145deg, rgba(212,175,55,0.14), rgba(11,19,32,0.96) 44%, rgba(42,67,101,0.92))", borderColor: "rgba(212,175,55,0.16)" }}>
                    <div className="grid gap-6 lg:grid-cols-[minmax(0,1fr)_340px] lg:items-end">
                        <div>
                            <div className="inline-flex items-center gap-2 text-[10px] uppercase tracking-[0.24em] text-white/52 mb-2.5" data-testid="discover-hero-kicker">
                                <Compass size={12} color="#D4AF37" /> Social planning hub
                            </div>
                            <h1 className="font-serif text-3xl sm:text-4xl lg:text-5xl leading-[1.02] tracking-tight max-w-3xl" data-testid="discover-hero-heading">
                                Discover real <span style={{ color: "var(--gold)" }}>Trips</span> and <span style={{ color: "#F4A28F" }}>Dates</span> created by the Passport community.
                            </h1>
                            <p className="text-xs sm:text-sm text-white/62 max-w-2xl mt-3 leading-relaxed" data-testid="discover-hero-copy">
                                Browse by destination, follow creators with great taste, and add proven plans to your own Passport without spending AI credits.
                            </p>
                        </div>
                        <div className="rounded-2xl p-4 sm:p-5" style={{ background: "rgba(5,10,17,0.58)", border: "1px solid rgba(255,255,255,0.08)" }}>
                            <div className="text-[10px] uppercase tracking-[0.22em] text-white/38 mb-2.5">This release</div>
                            <div className="grid gap-2.5 sm:grid-cols-3 lg:grid-cols-1">
                                {stats.map((stat) => (
                                    <div key={stat.label} className="rounded-xl px-3.5 py-2.5" style={{ background: "rgba(255,255,255,0.04)", border: "1px solid rgba(255,255,255,0.08)" }}>
                                        <div className="text-[10px] uppercase tracking-[0.2em] text-white/38">{stat.label}</div>
                                        <div className="font-serif text-2xl mt-0.5">{stat.value}</div>
                                    </div>
                                ))}
                            </div>
                            <div className="flex flex-wrap gap-2 mt-3">
                                <Link to="/concierge" className="btn-gold text-xs !px-3.5 !py-2" data-testid="discover-open-concierge">
                                    <Sparkle size={12} weight="fill" /> Passport Concierge
                                </Link>
                                <Link to="/traveldate" className="btn-ghost text-xs !px-3.5 !py-2" data-testid="discover-open-traveldate">
                                    <PaperPlaneTilt size={12} /> TravelDate Concierge
                                </Link>
                            </div>
                        </div>
                    </div>
                </section>

                <DiscoverToolbar
                    type={type}
                    setType={(next) => { setType(next); setCountryId(""); setCityId(""); }}
                    countries={countries}
                    countryId={countryId}
                    setCountryId={setCountryId}
                    cities={cities}
                    cityId={cityId}
                    setCityId={setCityId}
                    sort={sort}
                    setSort={setSort}
                />

                {state.loading ? (
                    <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
                        {Array.from({ length: 6 }).map((_, index) => (
                            <div key={index} className="rounded-[28px] aspect-[16/10] animate-pulse" style={{ background: "rgba(255,255,255,0.04)", border: "1px solid rgba(255,255,255,0.06)" }} />
                        ))}
                    </div>
                ) : state.items.length === 0 ? (
                    <DiscoverEmptyState {...EMPTY_COPY[type]} />
                ) : (
                    <div className="space-y-5">
                        <div className="flex flex-wrap items-center justify-between gap-3">
                            <div>
                                <div className="text-[10px] uppercase tracking-[0.22em] text-white/40">Community plans</div>
                                <div className="text-lg sm:text-xl font-medium">{type === "trip" ? "Trips" : "Dates"} {selectedCountry ? `· ${selectedCountry.country_name}` : "across Passport"}</div>
                            </div>
                            <div className="text-xs text-white/45">Sorted by <span className="text-white/78">{sort.replace(/_/g, " ")}</span></div>
                        </div>

                        <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
                            {state.items.map((item) => (
                                <DiscoverPlanCard
                                    key={item.source_id}
                                    item={item}
                                    onLike={handleLike}
                                    onAdd={handleAdd}
                                    onShare={handleShare}
                                    busy={!!busyMap[item.source_id]}
                                />
                            ))}
                        </div>

                        {state.nextCursor && (
                            <div className="text-center">
                                <button type="button" onClick={() => loadItems(state.nextCursor)} disabled={state.loadingMore} className="btn-ghost text-sm !py-2.5 !px-5" data-testid="discover-load-more">
                                    {state.loadingMore ? "Loading more plans…" : "Load more"}
                                </button>
                            </div>
                        )}
                    </div>
                )}
            </div>
        </div>
    );
}